// MoodboardAiRequestsApp.js
import React, { useEffect, useState, useRef } from 'react';
import PromptEditor from './PromptEditor';
import HistoryList from './HistoryList';
import SvgIcon from "../../common/SvgIcon";

export default function MoodboardAiRequestsApp({ image: initialImage, imageMask: initialImageMask, moodboardId = '', user, kind, moodboard }) {
  const [image, setImage] = useState(initialImage);
  const [imageMask, setImageMask] = useState(initialImageMask);
  const [prompt, setPrompt] = useState('');
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [initiatedHistory, setInitiatedHistory] = useState(false);
  const [generationInProgress, setGenerationInProgress] = useState(false);
  const [hideLoadMore, setHideLoadMore] = useState(false);
  const chatChannelRef = useRef(null);

  const [expandSignal, setExpandSignal] = useState(0);

  const sendExpandSignal = () => {
    setExpandSignal(prev => prev + 1);
  };

  useEffect(() => {
    fetchHistory(historyPage);
    subscribeToMoodboardChannel();

    return () => {
      if (chatChannelRef.current) {
        chatChannelRef.current.unsubscribe();
      }
    };
  }, []);

  const convertItem = (req, existing = {}) => {
    return {
      id: req.id,
      external_id: req.external_id,
      prompt: req.prompt || existing.prompt,
      status: req.status,
      created_at: req.created_at || existing.created_at,
      completed_at: req.completed_at,
      user: {
        avatarUrl: req.user && req.user.avatar_url || existing.user && existing.user.avatarUrl || '',
        initials: req.user && req.user.initials || existing.user && existing.user.initials || '',
      },
      moodboard: req.moodboard || existing.moodboard,
      image: req.image || existing.image,
      image_mask: req.image_mask || existing.image_mask,
      result_image: req.result_image,
    };
  };

  const onDelete = (item) => {
    const newHistory = [...history];
    const historyItem = newHistory.find(h => sameItemId(item, h));
    historyItem.deleteInProgress = true;
    setHistory(newHistory);
    const deletePromise = fetch(`/design/ai/requests/${item.id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    deletePromise.then(
      (response) => {
        if (response.ok) {
          setHistory(newHistory.filter(h => !sameItemId(item, h)));
        }
    });
    deletePromise.catch((err) => {
        console.error('Error on delete:', err);
    });
  };

  const onAddMoodboard = (item) => {
    const addMoodboardPromise = fetch(`/design/ai/requests/${item.id}/add_to_moodboard`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    addMoodboardPromise.then(
      (response) => {
        if (response.ok) {
          // do nothing???;
        }
      });
    addMoodboardPromise.catch((err) => {
      console.error('Error on delete:', err);
    });
  }

  const fetchHistory = () => {
    const apiUrl = `/design/ai/requests?page=${encodeURIComponent(historyPage)}&moodboard_id=${moodboardId}`;
    const apiCall = fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    apiCall
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to generate request');
        }
        return response.json();
      })
      .then((data) => {
        const reqs = data.ai_requests || [];
        const newHistoryItems = reqs.map(convertItem);
        setHistory((prev) => {
          const existingIds = new Set(prev.map(item => item.id));
          const filteredNewItems = newHistoryItems.filter(item => !existingIds.has(item.id));
          return [...prev, ...filteredNewItems];
        });
        setHistoryPage(historyPage + 1);
        setInitiatedHistory(true);
        setHideLoadMore(newHistoryItems.length < 20);
      })
      .catch((err) => {
        console.error('Error generating request:', err);
      });
  };

  const subscribeToMoodboardChannel = () => {
    chatChannelRef.current = App.cable.subscriptions.create(
      {
        channel: 'AiImageGenerationChannel',
        room: `user:${user.id}`,
      },
      {
        connected: function () {},
        disconnected: function () {},
        received: (data) => {
          if (data["event"] === "IMAGE_GENERATION_UPDATE") {
            onNewData(data["data"]["ai_request"])
            return;
          }
          if (data["event"] === "IMAGE_GENERATION_FAILED") {
            onNewData(data["data"]["ai_request"])
            return;
          }
        },
      },
    );
  };

  const sameItemId = (data, item) => {
    return data.id && data.id === item.id || data.external_id && data.external_id === item.external_id;
  };

  const onNewData = (data) => {
    setHistory(prev => {
      const existing = prev.find(h => sameItemId(data, h));
      if (existing) {
        return prev.map(h => (sameItemId(data, h) ? convertItem(data, h) : h));
      } else {
        return [convertItem(data), ...prev];
      }
    });
  };

  const onEditItem = (item) => {
    setImage(item.result_image);
    setPrompt(item.prompt);
    setImageMask(null);
    sendExpandSignal();
  };

  const onRegenerate = (item) => {
    setImage(item.image);
    setPrompt(item.prompt);
    setImageMask(null);
    sendExpandSignal();
  };

  const handleGenerate = (promptText, imageToProcess, imageMask, layout, quality) => {
    const externalId = Math.random().toString();
    const temporaryHistoryItem = {
      prompt: promptText,
      external_id: externalId,
      status: 'pending',
      user: {
        avatarUrl: user.avatar_url,
        initials: user.initials,
      },
      result_image: null,
      image: imageToProcess,
      image_mask: imageMask,
    };

    setHistory((prev) => [temporaryHistoryItem, ...prev]);
    setGenerationInProgress(true);

    const payload = {
      ai_request: {
        external_id: externalId,
        image_id: imageToProcess.id,
        image_mask_id: imageMask ? imageMask.id : null,
        layout: layout,
        quality: quality,
        moodboard_id: moodboardId,
        prompt: promptText,
        kind: 'moodboard_image',
      },
    };
    fetch('/design/ai/requests', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        setGenerationInProgress(false);
        if (!response.ok) {
          onNewData({ ...temporaryHistoryItem, status: 'failed' });
          throw new Error('Failed to generate request');
        }
        return response.json();
      })
      .then((data) => {
        const req = data.ai_request;

        const newHistoryItem = {
          id: req.id,
          prompt: req.prompt,
          status: req.status,
          created_at: req.created_at,
          external_id: req.external_id,
          user: {
            avatarUrl: req.user.avatar_url,
            initials: req.user.initials,
          },
          image: null, // filled in later
          result_image: null, // filled in later
        };
        onNewData(newHistoryItem);
      })
      .catch((err) => {
        console.error('Error generating request:', err);
        setGenerationInProgress(false);
      });
  };

  return (
    <div className="ai-image-generator-app">
      { moodboard && (
        <div className="moodboard-link">
            <SvgIcon icon="icon-link" />
            <a href={moodboard.url}>{moodboard.name}</a>
        </div>
      )}
      <PromptEditor
        image={image}
        imageMask={imageMask}
        initialPrompt={prompt}
        kind={kind}
        onGenerate={handleGenerate}
        expandSignal={expandSignal}
        // disabled={generationInProgress}
      />
      <HistoryList
        history={history}
        onLoadMore={fetchHistory}
        onGenerate={handleGenerate}
        onDelete={onDelete}
        onAddMoodboard={onAddMoodboard}
        onRegenerate={onRegenerate}
        onEditItem={onEditItem}
        initiated={initiatedHistory}
        hideLoadMore={hideLoadMore}
      />
    </div>
  );
}



// WEBPACK FOOTER //
// ./app/javascript/components/moodboard_ai_requests/index.js